import React, { Component } from 'react';
import { Query, Mutation } from 'react-apollo';
import PropTypes from 'prop-types';

import {
  GET_ENCYCLOPEDIA,
  DELETE_ARTICLE,
  CREATE_ARTICLE,
  UPDATE_ARTICLE,
  EXPORT_ENCYCLOPEDIA
} from './../../gql';

import Accordion from './../../components/Accordion';
import PagePagination from './../../components/PagePagination';
import Throbber from './../../components/Throbber';
import UnpublishedContentCheck from './../../components/UnpublishedContentCheck';
import { updateQueryEdges } from './../../utils/cache';
import Sections from './Sections';

import roundPlus from './../../../images/icons/round-plus-white.svg';
import bookmarkIcon from './../../../images/icons/bookmark-blue.svg';

class Encyclopaedia extends Component {
  constructor() {
    super();
    this.state = {
      articleUpdate: {},
      publishIsUpToDate: null,
      isCreating: false,
      createError: null,
      deletingId: null,
      deleteError: null
    };
    this.articles = [];
    this.activatePublishButton = this.activatePublishButton.bind(this);
    this.activateUnpublishedAlert = this.activateUnpublishedAlert.bind(this);
    this.onUnpublishedContent = this.onUnpublishedContent.bind(this);
  }

  queryVariables() {
    const { app, languageCode } = this.props;
    return {
      languageCode,
      app,
      limit: 1000,
    };
  }

  componentDidMount() {
    const { client } = this.props;
    client.writeData({
      data: {
        currentContentType: 'ARTICLE'
      }
    });
  }

  componentWillUnmount() {
    const { client } = this.props;
    client.writeData({
      data: {
        unpublishWarning: false,
      }
    });
  }

  activateUnpublishedAlert() {
    const { client } = this.props;
    client.writeData({
      data: {
        unpublishWarning: true,
      }
    });
  }

  componentDidUpdate(prevProps) {
    const { published } = this.props;

    if (!published && prevProps.published) {
      this.setState({
        publishIsUpToDate: false
      });
    }

    if (published && !prevProps.published) {
      this.setState({
        publishIsUpToDate: true
      });
    }
  }

  addArticleToCache(cache, article) {
    updateQueryEdges(cache, {
      query: GET_ENCYCLOPEDIA,
      variables: this.queryVariables(),
      field: 'encyclopedia'
    }, edges => [...edges, article]);
  }

  removeArticleFromCache(cache, id) {
    updateQueryEdges(cache, {
      query: GET_ENCYCLOPEDIA,
      variables: this.queryVariables(),
      field: 'encyclopedia'
    }, edges => edges.filter(article => article.id !== id));
  }

  deleteArticle(id) {
    const { client } = this.props;

    if (confirm('Are you sure you want to delete this article?')) {
      this.setState({ deletingId: id, deleteError: null });
      client.mutate({
        mutation: DELETE_ARTICLE,
        variables: { id },
        update: cache => this.removeArticleFromCache(cache, id)
      })
        .then(() => this.setState({ deletingId: null }))
        .catch(error => this.setState({
          deletingId: null,
          deleteError: { id, message: error.message }
        }));
      this.activatePublishButton();
      this.activateUnpublishedAlert();
    }
  }

  createArticle(articles) {
    const {
      app,
      client,
      languageCode
    } = this.props;

    const orderIndex = articles.length > 0
      ? articles[articles.length - 1].meta.displayOrder + 1
      : 0;

    this.setState({ isCreating: true, createError: null });
    client.mutate({
      mutation: CREATE_ARTICLE,
      variables: {
        title: '',
        subtitle: '',
        languageCode: languageCode,
        app: app,
        displayOrder: orderIndex
      },
      update: (cache, { data }) => this.addArticleToCache(cache, data.createArticle)
    })
      .then(() => this.setState({ isCreating: false }))
      .catch(error => this.setState({
        isCreating: false,
        createError: error.message
      }));
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  updateArticle(variables) {
    const { client } = this.props;

    // the mutation returns the updated article, which Apollo merges into the cache
    client.mutate({
      mutation: UPDATE_ARTICLE,
      variables
    }).catch(() => {
      // errors are logged by the errorLink
    });
  }

  activatePublishButton() {
    const { client } = this.props;
    client.writeData({
      data: {
        publishDisabled: false,
        queryRefetchAfterPublish: ['GET_ENCYCLOPEDIA'],
      }
    });
  }

  // Collapsed accordions don't mount their sections, so the whole tree is
  // checked here to enable the publish button for unpublished content
  onUnpublishedContent() {
    const { publishIsUpToDate } = this.state;

    if (publishIsUpToDate === null) {
      this.activatePublishButton();
    }
  }

  submitUpdate() {
    const { articleUpdate } = this.state;
    this.updateArticle(articleUpdate);
    this.setState({
      articleUpdate: {}
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  onChangeHandle(event, id) {
    const {
      app,
      languageCode,
    } = this.props;
    const name = event.target.name;
    const value = event.target.value;

    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.submitUpdate(), 500);

    this.setState(prevState => {
      if (prevState.articleUpdate && prevState.articleUpdate.id === id) {
        return {
          articleUpdate: {
            ...this.state.articleUpdate,
            id: id,
            [name]: value
          }
        };
      }
      return {
        articleUpdate: {
          languageCode,
          app,
          published: false,
          id,
          [name]: value
        }
      };
    });
  }

  updateDisplayOrder(id, order) {
    const {
      app,
      languageCode
    } = this.props;

    this.updateArticle({
      id,
      app,
      languageCode,
      published: false,
      displayOrder: order
    });
    this.activatePublishButton();
    this.activateUnpublishedAlert();
  }

  downloadEncyclopediaFile(url) {
    // download file from url 
    const pathname = new URL(url).pathname;
    const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

    // eslint-disable-next-line no-console
    console.log({ url })

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  }

  publishNotification() {
    const { publishIsUpToDate } = this.state;

    if (publishIsUpToDate === null) {
      this.activatePublishButton();
    } else if (publishIsUpToDate) {
      return;
    }

    return (
      <div className="notification notification--warning">
        <p className="notification__description">
          The content below has not been published yet.
        </p>
      </div>
    );
  }

  render() {
    const { app, languageCode, published } = this.props;
    const {
      isCreating,
      createError,
      deletingId,
      deleteError
    } = this.state;

    return (
      <div>
        <div className='right-align u-margin--bottom-large'>
          <Mutation
            update={(cache, { data }) => {
              this.downloadEncyclopediaFile(data.exportEncyclopediaToCSV)
            }}
            mutation={EXPORT_ENCYCLOPEDIA}
            variables={{
              languageCode: languageCode,
              app: app,
              published: published
            }}
          >
            {(exportEncyclopediaToCSV, { loading }) => (
              <div className='relative-block right-align'>
                <button
                  onClick={() => exportEncyclopediaToCSV()}
                  className='button button--link'
                >
                  Download entire encyclopedia
                </button>
                <Throbber
                  hidden={!loading}
                  loadingText='Downloading...'
                  alignRight
                />
              </div>
            )}
          </Mutation>
        </div>
        <Query
          query={GET_ENCYCLOPEDIA}
          variables={this.queryVariables()}
        >
          {({ data, loading, error, fetchMore }) => {
            if (loading)
              return <p className='full-width center-align'>Loading...</p>;
            if (error)
              return <p className='full-width center-align'>{error.message}</p>;
            const { encyclopedia } = data;
            this.articles = encyclopedia.edges;
            return (
              <div>
                <UnpublishedContentCheck
                  items={encyclopedia.edges}
                  childFields={['sections', 'subsections']}
                  onUnpublished={this.onUnpublishedContent}
                />
                {encyclopedia.edges.length > 0 ? (
                  <div>
                    {encyclopedia.edges
                      .sort((a, b) => a.meta.displayOrder - b.meta.displayOrder)
                      .map(article => (
                        <div key={article.id} className='relative-block'>
                          {deleteError && deleteError.id === article.id && (
                            <p className='full-width center-align'>
                              {deleteError.message}
                            </p>
                          )}
                          {!article.availability.published &&
                            this.publishNotification()
                          }
                          <Accordion
                            lazyChildren
                            showColorPicker
                            twoHeaderInputs
                            enableOrdering
                            enableChangeIcon
                            id={article.id}
                            title={article.title}
                            subtitle={article.subtitle}
                            titleColor={article.color}
                            headerIcon={article.icon || bookmarkIcon}
                            displayOrder={article.meta.displayOrder}
                            siblingAccordions={encyclopedia.edges}
                            updateDisplayOrder={(id, order) => this.updateDisplayOrder(id, order)}
                            onChanges={event =>
                              this.onChangeHandle(event, article.id)
                            }
                            deleteAccordion={() =>
                              this.deleteArticle(article.id)
                            }
                          >
                            <Sections
                              articleId={article.id}
                              sections={article.sections}
                              languageCode={languageCode}
                              published={published}
                              app={app}
                              activatePublishButton={
                                this.activatePublishButton
                              }
                              activateUnpublishedAlert={
                                this.activateUnpublishedAlert
                              }
                            />
                          </Accordion>
                          <Throbber
                            hidden={deletingId !== article.id}
                            loadingText='Deleting article...'
                          />
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className='full-width center-align'>No articles found.</p>
                )}
                <PagePagination
                  pageInfo={encyclopedia.pageInfo}
                  onPageClick={endCursor =>
                    fetchMore({
                      query: GET_ENCYCLOPEDIA,
                      variables: {
                        ...this.queryVariables(),
                        cursor: endCursor
                      },
                      updateQuery: (previousResult, { fetchMoreResult }) => {
                        const previous = previousResult.encyclopedia;
                        const newArticles = fetchMoreResult.encyclopedia.edges;

                        const newCursor =
                          fetchMoreResult.encyclopedia.pageInfo.endCursor;

                        // this is the updated data, and it must keep the same structure as the original data
                        return {
                          cursor: newCursor,
                          encyclopedia: {
                            edges: [...newArticles],
                            __typename: previous.__typename,
                            pageInfo: {
                              endCursor: newCursor,
                              ...fetchMoreResult.encyclopedia.pageInfo,
                              __typename: previous.pageInfo.__typename
                            }
                          }
                        };
                      }
                    })
                  }
                />
              </div>
            )
          }}
        </Query>
        <div className='full-width right-align relative-block'>
          <Throbber hidden={!isCreating} loadingText='Creating article...' />
          {createError && (
            <p className='full-width center-align'>{createError}</p>
          )}
          <button
            className='button button--small button--icon-text'
            disabled={isCreating}
            onClick={() => this.createArticle(this.articles)}
          >
            <img
              src={roundPlus}
              alt='Add new article'
              className='button__icon'
            />
            Add new article
          </button>
        </div>
      </div>
    );
  }
}

Encyclopaedia.propTypes = {
  app: PropTypes.string.isRequired,
  published: PropTypes.bool.isRequired,
  languageCode: PropTypes.string.isRequired,
  client: PropTypes.object.isRequired
};

export default Encyclopaedia;
